/**
 * Analytics Events Constants
 *
 * Centralized definition of all analytics events used in the application.
 * Use these constants instead of hardcoded strings to prevent typos and
 * maintain consistency across the codebase.
 *
 * Event Naming Convention: {context}_{element}_{action}
 * - All lowercase, snake_case
 * - Actions in past tense (e.g., "clicked", "viewed", "completed")
 *
 * @see Docs/internal/engineering/web/analytics for the canonical standard
 */

// ============================================================================
// SCREEN VIEWS
// ============================================================================

export const SCREEN_VIEW = 'screen_view' as const;

export const SCREEN_NAMES = {
  MARKETPLACE_HOME: 'marketplace_home',
  EXPLORE: 'explore',
  ARENA_SEARCH: 'arena_search',
  CT_LIST: 'ct_list',
  ARENA_DETAIL: 'arena_detail',
  BOOKING: 'booking',
  DAY_USE_SEARCH: 'day_use_search',
  RESERVATIONS: 'reservations',
  RESERVATION_DETAIL: 'reservation_detail',
  WORKOUTS: 'workouts',
  WORKOUT_DETAIL: 'workout_detail',
  MAKEUPS: 'makeups',
  MAKEUP_AGENDA: 'makeup_agenda',
  PLANS: 'plans',
  PLAN_DETAIL: 'plan_detail',
  MEMBERSHIPS: 'memberships',
  MEMBERSHIP_DETAIL: 'membership_detail',
  PROFILE: 'profile',
  LOGIN: 'login',
  SIGNUP: 'signup',
  FORGOT_PASSWORD: 'forgot_password',
  RESET_PASSWORD: 'reset_password',
  SUGGEST_ARENA: 'suggest_arena',
  TEACHERS_LANDING: 'teachers_landing',
  ARENAS_LANDING: 'arenas_landing',
  TOURNAMENT_EVENT: 'tournament_event',
  PAY_CHARGE: 'pay_charge',
  CONTRACT_TERMS: 'contract_terms',
  SIGNED_TERMS: 'signed_terms',
  ACCOUNT_DELETION: 'account_deletion',
  // Legal (static per audience)
  TERMS: 'terms',
  PRIVACY_POLICY: 'privacy_policy',
  ARENAS_TERMS: 'arenas_terms',
  ARENAS_PRIVACY_POLICY: 'arenas_privacy_policy',
  TRAINING_CENTERS_TERMS: 'training_centers_terms',
  TRAINING_CENTERS_PRIVACY_POLICY: 'training_centers_privacy_policy',
} as const;

export type ScreenName = typeof SCREEN_NAMES[keyof typeof SCREEN_NAMES];

// ============================================================================
// EVENTS
// ============================================================================

export const ANALYTICS_EVENTS = {
  // Component impressions
  COMPONENT_IMPRESSION: 'component_impression',

  // --------------------------------------------------------------------------
  // MARKETPLACE HOME
  // --------------------------------------------------------------------------
  HERO_BANNER_CTA_CLICKED: 'hero_banner_cta_clicked',
  LOCATION_SELECTED: 'location_selected',

  // Arena carousel
  ARENA_CAROUSEL_CARD_CLICKED: 'arena_carousel_card_clicked',
  ARENA_CAROUSEL_SEE_MORE_CLICKED: 'arena_carousel_see_more_clicked',

  // Category section
  CATEGORY_SECTION_CARD_CLICKED: 'category_section_card_clicked',

  // Upcoming reservations
  UPCOMING_RESERVATIONS_CAROUSEL_CARD_CLICKED: 'upcoming_reservations_carousel_card_clicked',
  UPCOMING_RESERVATIONS_CAROUSEL_SEE_ALL_CLICKED: 'upcoming_reservations_carousel_see_all_clicked',
  UPCOMING_RESERVATIONS_CAROUSEL_MAP_CLICKED: 'upcoming_reservations_carousel_map_clicked',

  // Day-use occurrences (on home)
  DAY_USE_OCCURRENCES_CARD_CLICKED: 'day_use_occurrences_card_clicked',
  DAY_USE_OCCURRENCES_SEE_MORE_CLICKED: 'day_use_occurrences_see_more_clicked',

  // Promo / featured
  PROMO_BANNER_CTA_CLICKED: 'promo_banner_cta_clicked',
  FEATURED_SECTION_ITEM_CLICKED: 'featured_section_item_clicked',

  // --------------------------------------------------------------------------
  // ARENA SEARCH
  // --------------------------------------------------------------------------
  ARENA_SEARCH_SEARCH_PERFORMED: 'arena_search_search_performed',
  ARENA_SEARCH_FILTER_OPENED: 'arena_search_filter_opened',
  ARENA_SEARCH_FILTER_APPLIED: 'arena_search_filter_applied',
  ARENA_SEARCH_FILTER_REMOVED: 'arena_search_filter_removed',
  ARENA_SEARCH_FILTER_CLEARED: 'arena_search_filter_cleared',
  ARENA_SEARCH_CARD_CLICKED: 'arena_search_card_clicked',

  // --------------------------------------------------------------------------
  // ARENA DETAIL
  // --------------------------------------------------------------------------
  ARENA_DETAIL_VIEWED: 'arena_detail_viewed',
  ARENA_DETAIL_BOOKING_CLICKED: 'arena_detail_booking_clicked',
  ARENA_DETAIL_CONTACT_CLICKED: 'arena_detail_contact_clicked',

  // --------------------------------------------------------------------------
  // BOOKING (CONVERSION FUNNEL)
  // --------------------------------------------------------------------------
  BOOKING_STARTED: 'booking_started',
  BOOKING_MODALITY_SELECTED: 'booking_modality_selected',
  BOOKING_SCHEDULE_SELECTED: 'booking_schedule_selected',
  BOOKING_PAYMENT_METHOD_SELECTED: 'booking_payment_method_selected',
  BOOKING_LOGIN_REQUIRED: 'booking_login_required',
  BOOKING_COMPLETED: 'booking_completed', // 🎯 conversion
  BOOKING_ABANDONED: 'booking_abandoned',
  BOOKING_ERROR: 'booking_error',

  // --------------------------------------------------------------------------
  // DAY USE — SEARCH
  // --------------------------------------------------------------------------
  DAY_USE_SEARCH_SEARCH_PERFORMED: 'day_use_search_search_performed',
  DAY_USE_SEARCH_FILTER_OPENED: 'day_use_search_filter_opened',
  DAY_USE_SEARCH_FILTER_APPLIED: 'day_use_search_filter_applied',
  DAY_USE_SEARCH_FILTER_REMOVED: 'day_use_search_filter_removed',
  DAY_USE_SEARCH_FILTER_CLEARED: 'day_use_search_filter_cleared',
  DAY_USE_SEARCH_CARD_CLICKED: 'day_use_search_card_clicked',

  // --------------------------------------------------------------------------
  // DAY USE — JOIN (CONVERSION FUNNEL)
  // --------------------------------------------------------------------------
  DAY_USE_DETAIL_VIEWED: 'day_use_detail_viewed',
  DAY_USE_JOIN_CLICKED: 'day_use_join_clicked',
  DAY_USE_JOIN_LOGIN_REQUIRED: 'day_use_join_login_required',
  DAY_USE_JOIN_COMPLETED: 'day_use_join_completed', // 🎯 conversion
  DAY_USE_JOIN_ERROR: 'day_use_join_error',

  // --------------------------------------------------------------------------
  // PLANS (SUBSCRIPTION — CONVERSION)
  // --------------------------------------------------------------------------
  PLAN_CARD_CLICKED: 'plan_card_clicked',
  PLAN_LOGIN_REQUIRED: 'plan_login_required',
  PLAN_PIX_KEY_COPIED: 'plan_pix_key_copied',
  PLAN_PAYMENT_INITIATED: 'plan_payment_initiated',
  PLAN_PAYMENT_COMPLETED: 'plan_payment_completed', // 🎯 conversion
  // Responsável com dependentes (Fase 2): só ids opacos (student_customer_id)
  // e contagens — nunca o nome do aluno.
  PLANS_STUDENT_FILTER_CHANGED: 'plans_student_filter_changed',

  // --------------------------------------------------------------------------
  // STANDING MEMBERSHIPS (ARENA "MENSALISTA" — CONVERSION)
  // --------------------------------------------------------------------------
  MEMBERSHIP_CARD_CLICKED: 'membership_card_clicked', // { membership_id, position, outstanding_count }
  MEMBERSHIP_LOGIN_REQUIRED: 'membership_login_required',
  MEMBERSHIP_PAYMENT_COMPLETED: 'membership_payment_completed', // 🎯 conversion — { membership_id }

  // --------------------------------------------------------------------------
  // WORKOUTS
  // --------------------------------------------------------------------------
  WORKOUTS_TAB_CHANGED: 'workouts_tab_changed',
  WORKOUT_CARD_CLICKED: 'workout_card_clicked',
  // Responsável com dependentes (Fase 2): idem regra de PII acima.
  WORKOUTS_STUDENT_FILTER_CHANGED: 'workouts_student_filter_changed',
  WORKOUT_PRESENCE_CONFIRMED: 'workout_presence_confirmed',
  WORKOUT_PRESENCE_CANCELLED: 'workout_presence_cancelled',

  // --------------------------------------------------------------------------
  // MAKEUPS (SELF-ENROLL — CONVERSION FUNNEL)
  // Funnel names mirror the mobile apps (`self_enroll_*`).
  // Params: ct_id, occurrence_id, source ("makeup_agenda"), block_reason
  // (raw backend reason) on the blocked event.
  // --------------------------------------------------------------------------
  MAKEUPS_TRAINING_CENTER_CARD_CLICKED: 'makeups_training_center_card_clicked',
  MAKEUP_AGENDA_DAY_SELECTED: 'makeup_agenda_day_selected',
  SELF_ENROLL_CHECKOUT_OPENED: 'self_enroll_checkout_opened',
  SELF_ENROLL_BLOCKED: 'self_enroll_blocked',
  SELF_ENROLL_COMPLETED: 'self_enroll_completed', // 🎯 conversion
  SELF_ENROLL_ERROR: 'self_enroll_error',

  // --------------------------------------------------------------------------
  // CLASS PACKAGES (PURCHASE FUNNEL)
  // Params: ct_id, source ('checkout' quando parte de uma aula, 'credits'
  // quando parte da tela de créditos), package_id, price_cents, class_count;
  // occurrence_id quando o funil partiu de uma aula. A inscrição que se segue
  // à compra sai como self_enroll_completed normal.
  // --------------------------------------------------------------------------
  CLASS_PACKAGE_OFFERED: 'class_package_offered',
  CLASS_PACKAGE_PURCHASE_STARTED: 'class_package_purchase_started',
  CLASS_PACKAGE_PIX_DISPLAYED: 'class_package_pix_displayed',
  CLASS_PACKAGE_PURCHASE_CONFIRMED: 'class_package_purchase_confirmed', // 🎯 conversion
  CLASS_PACKAGE_PURCHASE_EXPIRED: 'class_package_purchase_expired',

  // --------------------------------------------------------------------------
  // RESERVATIONS
  // --------------------------------------------------------------------------
  RESERVATIONS_TAB_CHANGED: 'reservations_tab_changed',
  RESERVATION_CARD_CLICKED: 'reservation_card_clicked',
  RESERVATION_PIX_OPENED: 'reservation_pix_opened',
  RESERVATION_PIX_KEY_COPIED: 'reservation_pix_key_copied',
  RESERVATION_PIX_CONFIRMED: 'reservation_pix_confirmed',
  RESERVATION_CANCEL_OPENED: 'reservation_cancel_opened',
  RESERVATION_CANCELLED: 'reservation_cancelled',
  RESERVATION_CANCEL_ABORTED: 'reservation_cancel_aborted',

  // --------------------------------------------------------------------------
  // PROFILE
  // --------------------------------------------------------------------------
  PROFILE_SHORTCUT_CLICKED: 'profile_shortcut_clicked',
  USER_LOGGED_OUT: 'user_logged_out',

  // --------------------------------------------------------------------------
  // AUTHENTICATION (CONVERSION)
  // --------------------------------------------------------------------------
  LOGIN_ATTEMPT: 'login_attempt',
  LOGIN_SUCCESS: 'login_success', // 🎯 conversion
  LOGIN_ERROR: 'login_error',
  SIGNUP_ATTEMPT: 'signup_attempt',
  SIGNUP_SUCCESS: 'signup_success', // 🎯 conversion
  SIGNUP_ERROR: 'signup_error',
  FORGOT_PASSWORD_SUBMITTED: 'forgot_password_submitted',
  FORGOT_PASSWORD_ERROR: 'forgot_password_error',
  RESET_PASSWORD_SUBMITTED: 'reset_password_submitted',
  RESET_PASSWORD_COMPLETED: 'reset_password_completed',
  RESET_PASSWORD_ERROR: 'reset_password_error',
  AUTH_NAV_CLICKED: 'auth_nav_clicked',
  AUTH_MODAL_OPENED: 'auth_modal_opened',

  // --------------------------------------------------------------------------
  // TOURNAMENT (public follow-along)
  // --------------------------------------------------------------------------
  TOURNAMENT_CATEGORY_CLICKED: 'tournament_category_clicked',

  // --------------------------------------------------------------------------
  // GLOBAL NAVIGATION
  // --------------------------------------------------------------------------
  NAV_CLICKED: 'nav_clicked',

  // --------------------------------------------------------------------------
  // DOWNLOAD APP BANNER (pay charge inline card + site-wide mobile smart
  // banner). Params: store ('app_store' | 'google_play'); screen is
  // auto-injected.
  // --------------------------------------------------------------------------
  DOWNLOAD_APP_BANNER_CLICKED: 'download_app_banner_clicked',
  DOWNLOAD_APP_BANNER_DISMISSED: 'download_app_banner_dismissed',

  // --------------------------------------------------------------------------
  // EXPLORE HUB (mirrors iOS/Android)
  // --------------------------------------------------------------------------
  EXPLORE_CATEGORY_CARD_CLICKED: 'explore_category_card_clicked',
  // Home-content banner that opens the Explore hub (header has no product nav).
  EXPLORE_BANNER_CLICKED: 'explore_banner_clicked',

  // --------------------------------------------------------------------------
  // USER PREFERENCES (PERSONALIZATION)
  // --------------------------------------------------------------------------
  PREFERENCES_BANNER_CLICKED: 'preferences_banner_clicked',
  PREFERENCES_BANNER_CLOSED: 'preferences_banner_closed',
  PREFERENCES_MODAL_VIEWED: 'preferences_modal_viewed',
  PREFERENCES_MODAL_SKIPPED: 'preferences_modal_skipped',
  PREFERENCES_MODAL_CLOSED: 'preferences_modal_closed',
  PREFERENCES_SAVED: 'preferences_saved', // engagement
  PREFERENCES_SAVE_ERROR: 'preferences_save_error',

  // --------------------------------------------------------------------------
  // PRIVACY / LGPD
  // --------------------------------------------------------------------------
  ANALYTICS_CONSENT_CHANGED: 'analytics_consent_changed',

  // --------------------------------------------------------------------------
  // CONTRACT TERMS (public acceptance page /terms/:token)
  // --------------------------------------------------------------------------
  CONTRACT_TERMS_OTP_SENT: 'contract_terms_otp_sent',
  CONTRACT_TERMS_OTP_VERIFIED: 'contract_terms_otp_verified',
  CONTRACT_TERMS_ACCEPTED: 'contract_terms_accepted',
  /** Banner do termo pendente/desatualizado no detalhe do plano. { plan_id, status } */
  CONTRACT_TERMS_BANNER_CLICKED: 'contract_terms_banner_clicked',
  /** Item de "Termos assinados" no detalhe do plano. { kind: 'general' | 'contract' } */
  CONTRACT_TERMS_RECEIPT_OPENED: 'contract_terms_receipt_opened',
} as const;

// Type helper for event names
export type AnalyticsEventName = typeof ANALYTICS_EVENTS[keyof typeof ANALYTICS_EVENTS];

// ============================================================================
// COMPONENT TYPES (for component_impression events)
// ============================================================================

export const COMPONENT_TYPES = {
  HERO_BANNER: 'hero_banner',
  ARENA_CAROUSEL: 'arena_carousel',
  CATEGORY_SECTION: 'category_section',
  UPCOMING_RESERVATIONS_CAROUSEL: 'upcoming_reservations_carousel',
  DAY_USE_OCCURRENCES: 'day_use_occurrences',
  PROMO_BANNER: 'promo_banner',
  EXPLORE_BANNER: 'explore_banner',
  FEATURED_SECTION: 'featured_section',
  // Result grids / list cards
  ARENA_SEARCH_RESULT: 'arena_search_result',
  DAY_USE_SEARCH_RESULT: 'day_use_search_result',
  WORKOUT_CARD: 'workout_card',
  MAKEUP_TRAINING_CENTER_CARD: 'makeup_training_center_card',
  MAKEUP_AGENDA_OCCURRENCE_CARD: 'makeup_agenda_occurrence_card',
  PLAN_CARD: 'plan_card',
  VENUE_MEMBERSHIP_CARD: 'venue_membership_card',
  RESERVATION_CARD: 'reservation_card',
  TOURNAMENT_CATEGORY: 'tournament_category',
} as const;

export type ComponentType = typeof COMPONENT_TYPES[keyof typeof COMPONENT_TYPES];
